<template>
    <div class="CWysiwyg" :class="{ disabled }" :data-compact="compact && hideMenu">
        <div class="input overflow-hidden px-0 relative">
            <div v-if="!isMounted" class="animate-pulse bg-gray-100 dark:bg-subtle rounded min-h-[4rem] p-4">
                <div class="h-4 bg-gray-200 dark:bg-subtle rounded w-3/4 mb-2"></div>
                <div class="h-4 bg-gray-200 dark:bg-subtle rounded w-1/2"></div>
            </div>
            <template v-else>
                <div
                    v-if="editor && menu.length"
                    class="flex divide-x divide-gray-450 dark:divide-default rounded-t-lg border-b border-gray-450 dark:border-default bg-gray-200 dark:bg-subtle"
                    :class="{ 'hidden': hideMenu }"
                >
                    <div v-for="(group, groupIndex) in menu" :key="groupIndex" class="flex p-1">
                        <button
                            v-for="(item, itemIndex) in group"
                            :key="itemIndex"
                            class="flex aspect-square w-9 cursor-pointer items-center justify-center rounded text-gray-750 dark:text-txt-body hover:bg-black/5 dark:hover:bg-white/10 hover:text-black dark:hover:text-white"
                            :class="{ 'bg-black/5 dark:bg-white/10': item.active }"
                            :title="item.title"
                            @click.prevent="item.command"
                        >
                            <CIcon :name="item.icon" :size="14" />
                        </button>
                    </div>
                </div>

                <EditorContent
                    v-if="editor"
                    :class="['CWysiwyg__content', WYSIWYG_BLOCK_CLASS, { 'CWysiwyg__content--menu-hidden': hideMenu }]"
                    :editor="editor"
                />
                <div v-else class="p-4 text-gray-400 dark:text-txt-body">Initializing editor...</div>

                <!-- Image upload indicator -->
                <div v-if="isUploading" class="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-surface-card/80 z-10">
                    <div class="flex items-center gap-2 text-primary-600 dark:text-primary-300">
                        <svg class="size-5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span class="text-sm font-medium">Nahrávam obrázok...</span>
                    </div>
                </div>

                <slot name="footer" />
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Extension, Node } from '@tiptap/core'
import { Heading } from '@tiptap/extension-heading'
import { Highlight } from '@tiptap/extension-highlight'
import { Image } from '@tiptap/extension-image'
import { Link } from '@tiptap/extension-link'
import { Mention } from '@tiptap/extension-mention'
import { Placeholder } from '@tiptap/extension-placeholder'
import { TextAlign } from '@tiptap/extension-text-align'
import { StarterKit } from '@tiptap/starter-kit'
import { Editor, EditorContent, mergeAttributes, VueRenderer } from '@tiptap/vue-3'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import tippy, { Instance } from 'tippy.js'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useVfm } from 'vue-final-modal'

import WysiwygMentionList from './wysiwyg/WysiwygMentionList.vue'
import CIcon from './CIcon.vue'
import { getCourseTableExtensions } from '@/utils/education/course-editor-extensions'

const WYSIWYG_BLOCK_CLASS = 'wysiwyg-text'

interface Props {
    modelValue?: string
    allowedButtons?: string[]
    enableLinks?: boolean
    enableCourseContent?: boolean
    enableImages?: boolean
    enableImagePaste?: boolean
    imageUploadPath?: string
    placeholder?: string
    disabled?: boolean
    hideMenu?: boolean
    autoFocus?: boolean
    compact?: boolean
    getMentionSuggestionItems?: (query: string) => { id: string; label: string }[] | Promise<{ id: string; label: string }[]>
}

interface MenuItem {
    title?: string
    icon: string
    command: () => void
    active?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: '',
    allowedButtons: () => [],
    enableLinks: false,
    enableCourseContent: false,
    enableImages: false,
    enableImagePaste: false,
    imageUploadPath: 'blogs/images',
    placeholder: '',
    disabled: false,
    hideMenu: false,
    autoFocus: false,
    compact: false,
    getMentionSuggestionItems: undefined
})

const emit = defineEmits(['update:modelValue', 'update', 'paste', 'focus'])
const editor = ref<Editor>()
const isMounted = ref(false)

const menu = computed((): MenuItem[][] => {
    if (!editor.value) return []

    const chain = () => editor.value!.chain().focus()
    const commands = () => editor.value!.commands
    const isActive = (attr: string, opts?: any) => editor.value!.isActive(attr, opts)
    const allowed = (btn: string) => props.allowedButtons.length === 0 || props.allowedButtons.includes(btn)

    const menuItems = [
        // Undo/Redo
        [
            allowed('back') ? { title: 'Vrátiť späť', command: () => commands().undo(), icon: 'editor-back' } : undefined,
            allowed('forward') ? { title: 'Zopakovať', command: () => commands().redo(), icon: 'editor-forward' } : undefined
        ].filter(Boolean),
        // Headings
        [
            allowed('h1') ? { title: 'Nadpis 1', command: () => chain().toggleHeading({ level: 1 }).run(), active: isActive('heading', { level: 1 }), icon: 'editor-h1' } : undefined,
            allowed('h2') ? { title: 'Nadpis 2', command: () => chain().toggleHeading({ level: 2 }).run(), active: isActive('heading', { level: 2 }), icon: 'editor-h2' } : undefined,
            allowed('h3') ? { title: 'Nadpis 3', command: () => chain().toggleHeading({ level: 3 }).run(), active: isActive('heading', { level: 3 }), icon: 'editor-h3' } : undefined
        ].filter(Boolean),
        // Text formatting
        [
            allowed('bold') ? { title: 'Tučné', command: () => chain().toggleBold().run(), active: isActive('bold'), icon: 'editor-bold' } : undefined,
            allowed('italic') ? { title: 'Kurzíva', command: () => chain().toggleItalic().run(), active: isActive('italic'), icon: 'editor-italic' } : undefined,
            allowed('strike') ? { title: 'Prečiarknuté', command: () => chain().toggleStrike().run(), active: isActive('strike'), icon: 'editor-strike' } : undefined,
            allowed('highlight') ? { title: 'Zvýrazniť', command: () => chain().toggleHighlight().run(), active: isActive('highlight'), icon: 'editor-highlight' } : undefined
        ].filter(Boolean),
        // Alignment
        [
            allowed('left') ? { title: 'Zarovnať vľavo', command: () => chain().setTextAlign('left').run(), active: isActive({ textAlign: 'left' }), icon: 'editor-align-left' } : undefined,
            allowed('center') ? { title: 'Zarovnať na stred', command: () => chain().setTextAlign('center').run(), active: isActive({ textAlign: 'center' }), icon: 'editor-align-center' } : undefined,
            allowed('right') ? { title: 'Zarovnať vpravo', command: () => chain().setTextAlign('right').run(), active: isActive({ textAlign: 'right' }), icon: 'editor-align-right' } : undefined
        ].filter(Boolean),
        // Lists
        [
            allowed('bulletList') ? { title: 'Odrážky', command: () => chain().toggleBulletList().run(), active: isActive('bulletList'), icon: 'editor-list-bullet' } : undefined,
            allowed('orderedList') ? { title: 'Číslovaný zoznam', command: () => chain().toggleOrderedList().run(), active: isActive('orderedList'), icon: 'editor-list-number' } : undefined
        ].filter(Boolean),
        // Block elements
        [
            allowed('blockquote') ? { title: 'Citát', command: () => chain().toggleBlockquote().run(), active: isActive('blockquote'), icon: 'editor-paragraph' } : undefined,
            allowed('callout') ? { title: 'Poznámka', command: () => toggleCallout(), active: isActive('callout'), icon: 'editor-callout' } : undefined,
            allowed('code') ? { title: 'Kód', command: () => chain().toggleCode().run(), active: isActive('code'), icon: 'editor-code' } : undefined,
            allowed('horizontalRule') ? { title: 'Oddeľovač', command: () => chain().setHorizontalRule().run(), icon: 'editor-hr' } : undefined
        ].filter(Boolean)
    ].filter((item) => item.length > 0) as MenuItem[][]

    if (props.enableLinks) {
        menuItems.push([{ title: 'Pridať odkaz', command: () => setLink(), active: isActive('link'), icon: 'editor-link' }])
    }

    if (props.enableImages) {
        menuItems.push([{ title: 'Pridať obrázok', command: () => setImage(), active: isActive('image'), icon: 'add-photo' }])
    }

    return menuItems
})

watch(
    () => props.modelValue,
    (value) => {
        if (!editor.value || editor.value.isDestroyed) return
        
        const currentValue = value || ''
        const currentEditorValue = editor.value?.getHTML() || ''

        const newCurrentValue = !currentValue.startsWith(`<div class="${WYSIWYG_BLOCK_CLASS}">`)
            ? `<div class="${WYSIWYG_BLOCK_CLASS}">${currentValue}</div>`
            : currentValue
        const newCurrentEditorValue = !currentEditorValue.startsWith(`<div class="${WYSIWYG_BLOCK_CLASS}">`)
            ? `<div class="${WYSIWYG_BLOCK_CLASS}">${currentEditorValue}</div>`
            : currentEditorValue

        if (newCurrentValue !== newCurrentEditorValue) {
            editor.value?.commands.setContent(newCurrentValue)
        }
    }
)

onBeforeUnmount(() => {
    if (editor.value && !editor.value.isDestroyed) {
        editor.value.destroy()
    }
    editor.value = undefined
})

onMounted(() => {
    isMounted.value = true

    if (editor.value) {
        editor.value.destroy()
        editor.value = undefined
    }

    try {
    editor.value = new Editor({
        extensions: [
            Link.configure({
                autolink: true,
                linkOnPaste: true,
                openOnClick: false,
                defaultProtocol: 'https'
            }),
            StarterKit.configure({
                heading: false,
                codeBlock: props.enableCourseContent ? {} : false,
                link: false // Using separate Link extension with custom config
            }),
            ...(props.enableCourseContent ? getCourseTableExtensions() : []),
            Image.extend({
                addOptions() {
                    return {
                        ...this.parent?.(),
                        sizes: ['inline', 'block', 'left', 'right']
                    }
                },
                renderHTML({ HTMLAttributes }) {
                    const { style } = HTMLAttributes
                    let float = ''
                    if (style) {
                        const align = style.split('text-align: ')[1]
                        if (align === 'justify') {
                            float = `;float: left`
                        } else if (align === 'right') {
                            float = `;float: ${align}`
                        }
                    }

                    return [
                        'figure',
                        { style: style + float },
                        [
                            'img',
                            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
                                style: 'display: inline'
                            })
                        ]
                    ]
                }
            }),
            Heading,
            Highlight.configure({
                multicolor: false,
                HTMLAttributes: {
                    class: 'bg-yellow-200 dark:bg-yellow-500/40 px-0.5 rounded'
                }
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph', 'image']
            }),
            Placeholder.configure({
                placeholder: props.placeholder
            }),
            Mention.configure({
                HTMLAttributes: {
                    class: 'mention'
                },

                renderText({ options, node }) {
                    return `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`
                },

                renderHTML({ options, node }) {
                    return [
                        'a',
                        mergeAttributes(options.HTMLAttributes),
                        `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`
                    ]
                },

                suggestion: {
                    allow: () => !!props.getMentionSuggestionItems,

                    items: ({ query }) => {
                        if (!props.getMentionSuggestionItems) return []
                        return props.getMentionSuggestionItems(query)
                    },

                    render: () => {
                        let component: VueRenderer | undefined
                        let popup: Instance[] | undefined

                        return {
                            onStart: (props) => {
                                component = new VueRenderer(WysiwygMentionList, {
                                    props
                                })

                                if (!props.clientRect) {
                                    return
                                }

                                popup = tippy('body', {
                                    getReferenceClientRect: () => {
                                        return props.clientRect?.() || new DOMRect()
                                    },
                                    appendTo: () => document.body,
                                    content: component.element,
                                    showOnCreate: true,
                                    interactive: true,
                                    trigger: 'manual',
                                    placement: 'bottom-start',
                                    animation: false
                                })
                            },

                            onUpdate(props) {
                                component?.updateProps(props)

                                if (!props.clientRect) return

                                popup?.[0].setProps({
                                    getReferenceClientRect: () => {
                                        return props.clientRect?.() || new DOMRect()
                                    }
                                })
                            },

                            onKeyDown(props) {
                                if (props.event.key === 'Escape') {
                                    popup?.[0].hide()

                                    return true
                                }

                                const componentRef = component?.ref as any
                                return componentRef?.onKeyDown(props)
                            },

                            onExit() {
                                popup?.[0].destroy()
                                component?.destroy()
                            }
                        }
                    }
                }
            }),
            // Callout/Alert blocks
            Callout,
            // YouTube embed support
            Youtube,
            // Custom paste handler for images and YouTube URLs
            createPasteHandlerExtension()
        ],

        content: props.modelValue,
        autofocus: props.autoFocus,

        onUpdate: (props) => {
            const html = props.editor.getHTML()
            const content = html.startsWith(`<div class="${WYSIWYG_BLOCK_CLASS}">`) 
                ? html 
                : `<div class="${WYSIWYG_BLOCK_CLASS}">${html}</div>`
            emit('update:modelValue', content)
            emit('update', props)
        },

        onFocus: () => {
            emit('focus')
        }
    })
    } catch (e) {
        console.warn('CWysiwyg: Editor initialization failed, retrying...', e)
        // Retry on next tick — previous editor's plugins may not be fully cleaned up
        nextTick(() => {
            try {
                editor.value = new Editor({
                    extensions: [
                        Link.configure({ autolink: true, linkOnPaste: true, openOnClick: false, defaultProtocol: 'https' }),
                        StarterKit.configure({ heading: false, codeBlock: false, link: false }),
                        Image,
                        Heading,
                        Placeholder.configure({ placeholder: props.placeholder })
                    ],
                    content: props.modelValue,
                    onUpdate: (p) => {
                        const html = p.editor.getHTML()
                        const content = html.startsWith(`<div class="${WYSIWYG_BLOCK_CLASS}">`)
                            ? html
                            : `<div class="${WYSIWYG_BLOCK_CLASS}">${html}</div>`
                        emit('update:modelValue', content)
                        emit('update', p)
                    }
                })
            } catch (e2) {
                console.error('CWysiwyg: Editor initialization failed on retry', e2)
            }
        })
    }
})

const calloutTypes = ['info', 'warning', 'success', 'error'] as const

const toggleCallout = (): void => {
    if (!editor.value) return

    const { state } = editor.value
    const { from } = state.selection

    // Find if we're inside a callout and get its current type
    let currentType: string | null = null
    state.doc.nodesBetween(from, from, (node) => {
        if (node.type.name === 'callout') {
            currentType = node.attrs.type
        }
    })

    if (currentType) {
        // Cycle to next type
        const currentIndex = calloutTypes.indexOf(currentType as typeof calloutTypes[number])
        const nextIndex = (currentIndex + 1) % calloutTypes.length

        if (nextIndex === 0) {
            // If we cycled back to info, remove the callout
            ;(editor.value.commands as any).unsetCallout()
        } else {
            // Update to next type - need to unwrap and rewrap
            ;(editor.value.commands as any).unsetCallout()
            ;(editor.value.commands as any).setCallout({ type: calloutTypes[nextIndex] })
        }
    } else {
        // Not in a callout, create one with 'warning' type (most common use)
        ;(editor.value.commands as any).setCallout({ type: 'warning' })
    }
}

const setLink = (): boolean => {
    const previousUrl = editor.value!.getAttributes('link').href
    const url = window.prompt('Vložte link alebo nechajte prázdne pole pre odstránenie.', previousUrl)

    if (url === null) return false
    if (url === '') {
        editor.value!.chain().focus().extendMarkRange('link').unsetLink().run()
        return false
    }

    editor.value!.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    return true
}

const setImage = async (): Promise<boolean> => {
    const vfm = useVfm()
    try {
        const selectedImage = await vfm.open('MediaLibrarySelectModal')

        if (selectedImage) {
            editor.value?.chain().focus().setImage({ src: selectedImage }).run()
            return true
        }
        return false
    } catch (error) {
        console.error('Error selecting image:', error)
        return false
    }
}

const isUploading = ref(false)

const extractYoutubeId = (text: string): string | null => {
    const pattern = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/
    const match = text.match(pattern)
    return match ? match[1] : null
}

const isYoutubeUrl = (text: string): boolean => {
    return /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)[a-zA-Z0-9_-]{11}([?&]\S*)?$/.test(text.trim())
}

// Callout/Alert block for warnings, info notes, etc.
const Callout = Node.create({
    name: 'callout',
    group: 'block',
    content: 'block+',
    defining: true,

    addAttributes() {
        return {
            type: {
                default: 'info',
                parseHTML: element => element.getAttribute('data-callout-type') || 'info',
                renderHTML: attributes => ({ 'data-callout-type': attributes.type })
            }
        }
    },

    parseHTML() {
        return [{ tag: 'div[data-callout]' }]
    },

    renderHTML({ HTMLAttributes }) {
        const type = HTMLAttributes['data-callout-type'] || 'info'
        const typeStyles: Record<string, string> = {
            info: 'border-blue-400 bg-blue-50 dark:bg-blue-950/30',
            warning: 'border-amber-400 bg-amber-50 dark:bg-amber-950/30',
            success: 'border-green-400 bg-green-50 dark:bg-green-950/30',
            error: 'border-red-400 bg-red-50 dark:bg-red-950/30'
        }
        const icons: Record<string, string> = {
            info: 'i',
            warning: '!',
            success: '\u2713',
            error: '\u2717'
        }
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-callout': '',
                class: `callout callout-${type} border-l-4 ${typeStyles[type]} p-4 my-4 rounded-r-lg`
            }),
            [
                'div',
                { class: 'flex gap-3' },
                [
                    'span',
                    { class: 'callout-icon font-bold text-lg shrink-0', 'data-callout-icon': type },
                    icons[type]
                ],
                ['div', { class: 'callout-content flex-1 min-w-0' }, 0]
            ]
        ]
    },

    addCommands() {
        return {
            setCallout: (attributes) => ({ commands }) => {
                return commands.wrapIn(this.name, attributes)
            },
            toggleCallout: (attributes) => ({ commands, state }) => {
                const { from, to } = state.selection
                const nodeAtPos = state.doc.nodeAt(from)

                // Check if we're inside a callout
                let insideCallout = false
                state.doc.nodesBetween(from, to, (node) => {
                    if (node.type.name === 'callout') {
                        insideCallout = true
                    }
                })

                if (insideCallout) {
                    return commands.lift(this.name)
                }
                return commands.wrapIn(this.name, attributes)
            },
            unsetCallout: () => ({ commands }) => {
                return commands.lift(this.name)
            }
        }
    }
})

// YouTube embed node for inline video embeds
const Youtube = Node.create({
    name: 'youtube',
    group: 'block',
    atom: true,

    addAttributes() {
        return {
            videoId: {
                default: null
            }
        }
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-youtube-video]',
                getAttrs: (node) => {
                    if (typeof node === 'string') return false
                    return { videoId: (node as HTMLElement).getAttribute('data-youtube-video') }
                }
            }
        ]
    },

    renderHTML({ HTMLAttributes }) {
        const videoId = HTMLAttributes.videoId
        return [
            'div',
            {
                'data-youtube-video': videoId,
                class: 'youtube-embed',
                style: 'position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 1rem 0;'
            },
            [
                'iframe',
                {
                    src: `https://www.youtube.com/embed/${videoId}`,
                    frameborder: '0',
                    allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
                    allowfullscreen: 'true',
                    style: 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 8px;'
                }
            ]
        ]
    }
})

const handlePastedImage = async (file: File): Promise<void> => {
    if (!props.enableImagePaste || isUploading.value) return
    await insertImageFile(file)
}

const insertImageFile = async (file: File): Promise<void> => {
    if (!editor.value || isUploading.value) return

    const { uploadFileToStorage } = await import('@/utils/uploadFileToStorage')

    isUploading.value = true
    try {
        const url = await uploadFileToStorage(file, props.imageUploadPath)
        editor.value?.chain().focus().setImage({ src: url }).run()
    } catch (error) {
        console.error('Failed to upload image:', error)
    } finally {
        isUploading.value = false
    }
}

// Create custom paste handler extension
const createPasteHandlerExtension = () => {
    return Extension.create({
        name: 'pasteHandler',
        addProseMirrorPlugins() {
            const uploadImage = handlePastedImage
            const enableImagePaste = props.enableImagePaste
            const getEditor = () => editor.value

            return [
                new Plugin({
                    key: new PluginKey('pasteHandler'),
                    props: {
                        handlePaste(view, event) {
                            const clipboardData = event.clipboardData
                            if (!clipboardData) return false

                            // Check for pasted images
                            const items = Array.from(clipboardData.items)
                            const imageItem = items.find(item => item.type.startsWith('image/'))

                            if (imageItem && enableImagePaste) {
                                const file = imageItem.getAsFile()
                                if (file) {
                                    uploadImage(file)
                                    return true // Prevent default paste
                                }
                            }

                            // Check for YouTube URLs - insert embed directly
                            const text = clipboardData.getData('text/plain').trim()
                            if (text && isYoutubeUrl(text)) {
                                const youtubeId = extractYoutubeId(text)
                                if (youtubeId) {
                                    const ed = getEditor()
                                    if (ed) {
                                        ed.chain().focus().insertContent({
                                            type: 'youtube',
                                            attrs: { videoId: youtubeId }
                                        }).run()
                                    }
                                    return true // Prevent default paste
                                }
                            }

                            return false // Let TipTap handle other pastes
                        }
                    }
                })
            ]
        }
    })
}

defineExpose({
    isUploading,
    editor,
    insertImageFile
})
</script>
